import React, { useEffect, useMemo } from 'react';
import { CheckCircle2, Sparkles } from 'lucide-react';
import { COUNTRIES_BY_PATH } from './constants';

/**
 * Step 1 — Choose sales path.
 * ★ 2026-08-13 (Fase 51 · Compliance CONSOB) — Rimosse le opzioni "Equity Share"
 *   e "Entrambi" dal modulo pubblico. Ora il flow parte automaticamente su
 *   `real_estate` e questo step diventa visibile solo se `config.paths` contiene
 *   più opzioni Real Estate (es. tipologie diverse). Se contiene solo l'opzione
 *   Equity/Both, questo step viene auto-avanzato.
 */
export function PathStep({ config, form, setForm }) {
  // Filtro paths per NON mostrare più equity_share/both sul pubblico.
  // ★ Round 2 qualità — memoizzato (identità stabile per le hook deps).
  const visiblePaths = useMemo(
    () => (config.paths || []).filter(
      (p) => p.id === 'real_estate' || p.id === 'realestate',
    ),
    [config.paths],
  );

  // Auto-select real_estate al primo mount se nulla è selezionato.
  // ★ Round 2 qualità — deps complete; guardia `!form.path` rende l'effetto
  // idempotente (dopo la prima selezione è un no-op).
  useEffect(() => {
    if (!form.path && visiblePaths.length > 0) {
      const rePath = visiblePaths.find((p) => p.id === 'real_estate' || p.id === 'realestate');
      if (rePath) {
        setForm((f) => ({
          ...f,
          path: rePath.id,
          budget_bracket: '',
          countries: (f.countries || []).filter((c) =>
            (COUNTRIES_BY_PATH[rePath.id] || []).includes(c),
          ),
        }));
      }
    }
  }, [form.path, setForm, visiblePaths]);

  return (
    <div className="space-y-4" data-testid="step-path">
      <div>
        <div className="flex items-center gap-2 text-[var(--gp-gold)]">
          <Sparkles className="w-4 h-4" />
          <span className="text-[10px] tracking-[0.3em] uppercase font-medium">Step 1 / 4</span>
        </div>
        <h3 className="font-serif text-2xl text-[hsl(var(--primary))] mt-1">Real Estate</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Acquisto di un appartamento in resort premium con piano pagamenti rateale.
          Disponibile in Albania (La Mar Residence, Narea) e Indonesia (Lombok).
        </p>
      </div>
      <div className="space-y-2.5">
        {visiblePaths.map((p) => {
          const sel = form.path === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setForm((f) => ({
                ...f,
                path: p.id,
                budget_bracket: '',
                countries: (f.countries || []).filter((c) =>
                  (COUNTRIES_BY_PATH[p.id] || []).includes(c),
                ),
              }))}
              className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                sel
                  ? 'border-[var(--gp-gold)] bg-[var(--gp-gold)]/10 shadow-md'
                  : 'border-border bg-[var(--surface-2)] hover:border-[var(--gp-gold)]/50'
              }`}
              data-testid={`qf-path-${p.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-serif text-lg text-[hsl(var(--primary))]">{p.label}</div>
                  <div className="text-sm text-muted-foreground mt-0.5">{p.desc}</div>
                </div>
                {sel && <CheckCircle2 className="w-5 h-5 text-[var(--gp-gold)]" />}
              </div>
            </button>
          );
        })}
        {visiblePaths.length === 0 && (
          <div className="p-4 rounded-xl bg-[var(--gp-gold)]/10 border border-[var(--gp-gold)]/40 text-sm text-muted-foreground">
            Real Estate — l&apos;acquisto dell&apos;appartamento è già selezionato.
            Procedi al prossimo step.
          </div>
        )}
      </div>
    </div>
  );
}
